import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ViewIcon,
  InfoIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import { IoStopCircleOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { motion } from "framer-motion";

import { chargePointCodeState } from "atoms/chargePointCodeState";
import { chargePointConnectorIdState } from "atoms/chargePointConnectorState";
import { formattedIstDate } from "utils/utils";
import useAxios from "axios-hooks";
import { toastState } from "atoms/toastState";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";

// Motion components
const MotionBox = motion(Box);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.85)",
    "rgba(26, 32, 44, 0.85)",
  );
  const borderColor = useColorModeValue(
    "rgba(245, 125, 54, 0.3)", // Theme color with opacity
    "rgba(245, 125, 54, 0.2)",
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(12px)"
      borderRadius="xl"
      boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.15)"
      // border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "0 8px 32px 0 rgba(245, 125, 54, 0.15)" }}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // background:
        // "linear-gradient(120deg, rgba(245, 125, 54, 0.05) 0%, rgba(255, 255, 255, 0) 70%)",
        pointerEvents: "none",
        zIndex: 0,
      }}
      position="relative"
      {...rest}
    >
      {children}
    </Box>
  );
};

const OngoingSessionsColumnsTable = (props) => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const chargePointCode = useRecoilValue(chargePointCodeState);
  const chargePointConnectorId = useRecoilValue(chargePointConnectorIdState);
  const { columnsData, tableData, refresh } = props;
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const [searchQuery, setSearchQuery] = useState("");
  const [
    { data: stopData, loading: stopLoading, error: stopError },
    executeStopSession,
    cancelStopSession,
  ] = useAxios({ method: "POST" }, { manual: true });

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const id = row.original.Id?.toString() || "";
          const evseId = row.original.EvseId?.toString() || "";
          const connectorId = row.original.ConnectorId?.toString() || "";

          return (
            id.toLowerCase().includes(lowercasedQuery) ||
            evseId.toLowerCase().includes(lowercasedQuery) ||
            connectorId.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    setGlobalFilter,
    gotoPage,
    setPageSize,
  } = tableInstance;

  const { canPreviousPage, canNextPage, pageCount, nextPage, previousPage } =
    tableInstance;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };
  const navigateToChargePointDetails = (session) => {
    const stationId = session.EvseId;
    const connectorId = session.ConnectorId;
    navigate(
      `/admin/locations/view/${session.LocationId}/stations?sid=${stationId}&cid=${connectorId}`,
    );

    // navigate(
    //   `/admin/chargingstations/view/${chargingStationId}/chargepoints?chargepoint=${chargePointCode}&connector=${chargePointConnectorId}`
    // );
  };

  const stopFaultySession = async (session) => {
    try {
      const response = await executeStopSession({
        method: "POST",
        url: `/api/ocpp16/stopFaultySession/${session.Id}`,
      });
      if (response.status === 200) {
        setToast({
          isOpen: true,
          title: `Session stopped successfully`,
          status: "success",
        });
      }
      refresh();
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: `Error occured while stopping session`,
          status: "error",
        });
      }
    }
  };

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");
  const themeColor = "#f57d36";

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
      mb={5}
      bg="transparent"
    >
      <GlassMorphismCard
        direction="column"
        w="100%"
        p={0}
        overflowX={{ sm: "scroll", lg: "hidden" }}
      >
        {/* Search Section */}
        <Box
          p={6}
          borderBottom="1px solid"
          borderColor={borderColor}
          position="relative"
          zIndex={1}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <HStack spacing={2} mb={1}>
                <Icon as={SearchIcon} color={themeColor} boxSize={3} />
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Search Sessions
                </Text>
              </HStack>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={themeColor} opacity={0.6} />
                </InputLeftElement>
                <Input
                  placeholder="Search by ID, charge point, connector..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="xl"
                  width="350px"
                  bg={useColorModeValue("white", "gray.700")}
                  border="1px solid"
                  borderColor={useColorModeValue("gray.200", "gray.600")}
                  fontSize="sm"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                  _hover={{
                    borderColor: "gray.300",
                  }}
                  transition="all 0.2s"
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Ongoing Sessions
              </Text>
              <Badge
                colorScheme="orange"
                ml={2}
                fontSize="xs"
                borderRadius="full"
                px={2}
              >
                {data.length} total
              </Badge>
            </Flex>
          </Flex>
        </Box>
        <TableContainer>
          <Table
            {...getTableProps()}
            variant="simple"
            colorScheme="orange"
            position="relative"
            zIndex={1}
          >
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                    // transition={{ duration: 0.2 }}
                    whileHover={{ y: -1 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "ChargingSessionId") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="orange"
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                              boxShadow="0 2px 5px rgba(245, 125, 54, 0.15)"
                            >
                              {cell.row.original.Id}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "ChargePoint") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell.row.original.EvseId}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Connector") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="blue"
                              variant="outline"
                              borderRadius="full"
                              px={2}
                              py={0.5}
                              boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                            >
                              {cell.row.original.ConnectorId}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Start-Time") {
                        data = (
                          <Tooltip
                            label={formattedIstDate(
                              cell.row.original.StartTime,
                            )}
                            placement="top"
                            hasArrow
                          >
                            <Flex align="center" justify="center">
                              <Icon
                                as={TimeIcon}
                                mr={1}
                                color={themeColor}
                                opacity={0.7}
                              />
                              <Text
                                color={textColor}
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                {formattedIstDate(cell.row.original.StartTime)}
                              </Text>
                            </Flex>
                          </Tooltip>
                        );
                      } else if (cell.column.Header === "Dispatched-Units") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="green"
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                              boxShadow="0 2px 5px rgba(72, 187, 120, 0.15)"
                              fontWeight="semibold"
                            >
                              {cell.row.original.UnitsConsumed.toFixed(3)} kWh
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Actions") {
                        //get last updated at and check if it is greater than 10 minutes
                        const lastUpdatedAt = cell.row.original.UpdatedAt;
                        const timeDiff = new Date() - new Date(lastUpdatedAt);
                        const minutes =
                          Math.floor((timeDiff / 60000) * 100) / 100;
                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon boxSize={4} />}
                                colorScheme="blue"
                                variant="ghost"
                                size="md"
                                borderRadius="full"
                                aria-label="View"
                                _hover={{
                                  bg: "blue.50",
                                  transform: "scale(1.1)",
                                  transition: "all 0.2s",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToChargePointDetails(
                                    cell.row.original,
                                  );
                                }}
                              />
                            </Tooltip>

                            {minutes > 3 && (
                              <RoleBasedRender roles={onlyUAdmins}>
                                <Tooltip
                                  label="Stop Session"
                                  placement="top"
                                  hasArrow
                                >
                                  <IconButton
                                    colorScheme="red"
                                    variant="ghost"
                                    size="md"
                                    borderRadius="full"
                                    aria-label="Stop"
                                    isLoading={stopLoading}
                                    _hover={{
                                      bg: "red.50",
                                      transform: "scale(1.1)",
                                      transition: "all 0.2s",
                                    }}
                                    icon={
                                      <Icon
                                        as={IoStopCircleOutline}
                                        boxSize={5}
                                      />
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      stopFaultySession(cell.row.original);
                                    }}
                                  />
                                </Tooltip>
                              </RoleBasedRender>
                            )}
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                          textAlign="center"
                          transition="background 0.2s"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {data.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
              _hover={{
                transform: canPreviousPage ? "translateY(-2px)" : "none",
                shadow: canPreviousPage ? "md" : "none",
              }}
              transition="all 0.2s"
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </Button>

            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
              _hover={{
                transform: canPreviousPage ? "translateY(-2px)" : "none",
                shadow: canPreviousPage ? "md" : "none",
              }}
              transition="all 0.2s"
            >
              <ChevronLeftIcon boxSize={4} />
            </Button>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={pageIndex === i ? "solid" : "outline"}
                  colorScheme="orange"
                  onClick={() => gotoPage(i)}
                  _hover={{ transform: "translateY(-2px)", shadow: "md" }}
                  transition="all 0.2s"
                  minW="40px"
                >
                  {i + 1}
                </Button>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => gotoPage(0)}
                    minW="40px"
                  >
                    1
                  </Button>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => gotoPage(pageIndex - 1)}
                    minW="40px"
                  >
                    {pageIndex}
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="solid"
                  colorScheme="orange"
                  onClick={() => gotoPage(pageIndex)}
                  minW="40px"
                >
                  {pageIndex + 1}
                </Button>

                {pageIndex < pageCount - 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => gotoPage(pageIndex + 1)}
                    minW="40px"
                  >
                    {pageIndex + 2}
                  </Button>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => gotoPage(pageCount - 1)}
                    minW="40px"
                  >
                    {pageCount}
                  </Button>
                )}
              </>
            )}

            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
              _hover={{
                transform: canNextPage ? "translateY(-2px)" : "none",
                shadow: canNextPage ? "md" : "none",
              }}
              transition="all 0.2s"
            >
              <ChevronRightIcon boxSize={4} />
            </Button>

            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
              _hover={{
                transform: canNextPage ? "translateY(-2px)" : "none",
                shadow: canNextPage ? "md" : "none",
              }}
              transition="all 0.2s"
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </Button>
          </HStack>
        </Flex>
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default OngoingSessionsColumnsTable;
